import React, { useCallback, useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, CircularProgress, Button, Typography, Box } from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { usePostPaymentsIntentsMutation, usePutPaymentsByPaymentIntentIdMutation } from "../../../redux/residentPortalApi";
import config from '../../../config';
import { getPaymentReturnUrl } from '../../../utils/paymentReturnUrl';

const stripePromise = loadStripe(config.STRIPE_KEY);

interface SubscribeModalProps {
  open: boolean;
  onClose: () => void;
  selectedPackageId: string;
  selectedPackageName: string;
  propertyId: string;
  amountCents: number;
  currency?: string;
}

const CheckoutForm: React.FC<{ clientSecret: string, onSuccess?: () => void }> = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [finalize] = usePutPaymentsByPaymentIntentIdMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error, paymentIntent } = await stripe!.confirmPayment({ elements: elements!, redirect: 'if_required' });
    if (error) {
      setMessage(error.message ?? 'Payment failed.');
      setLoading(false);
      return;
    }

    try {
      if (paymentIntent?.id && paymentIntent.status === 'succeeded') {
        await finalize({ paymentIntentId: paymentIntent.id, body: { status: 'succeeded' } }).unwrap();
        setMessage('Payment successful.');
        onSuccess?.();
      } else {
        setMessage(`Payment status: ${paymentIntent?.status ?? 'unknown'}`);
      }
    } catch (e) {
      setMessage('Failed to finalize payment.');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {message && <Typography color="error">{message}</Typography>}
      <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ mt: 2 }}>
        {loading ? <CircularProgress size={20} /> : 'Confirm Payment'}
      </Button>
    </form>
  );
};

const SubscribeModal: React.FC<SubscribeModalProps> = ({ open, onClose, selectedPackageId, selectedPackageName, propertyId, amountCents, currency = 'usd' }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [createIntent] = usePostPaymentsIntentsMutation();

  const requestClientSecret = useCallback(async () => {
    if (!open || !selectedPackageId) return;
    if (!propertyId || !amountCents || amountCents <= 0) {
      setError('Invalid package or price.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const provider = (config.PAYMENT_PROCESSOR === 'stripe' || config.PAYMENT_PROCESSOR === 'paynow') ? config.PAYMENT_PROCESSOR : 'stripe';
      const returnUrl = getPaymentReturnUrl(propertyId);
      const body = {
        amount: amountCents,
        currency: provider === 'paynow' ? (currency?.toUpperCase() || 'USD') : (currency || 'usd'),
        purpose: 'internet',
        propertyId,
        provider: provider as 'stripe' | 'paynow',
        returnUrl,
        internetPackageId: selectedPackageId,
      };
      const response = await createIntent({ body } as any).unwrap();
      if ((response as any)?.clientSecret) {
        setClientSecret((response as any).clientSecret);
      } else {
        setError('Failed to initialize payment.');
        setClientSecret(null);
      }
    } catch (err) {
      console.error('Failed to create payment intent:', err);
      setError('Failed to create payment. Please try again.');
      setClientSecret(null);
    } finally {
      setLoading(false);
    }
  }, [open, selectedPackageId, amountCents, propertyId, currency, createIntent]);

  useEffect(() => {
    // Reset state when modal opens/closes or package changes
    if (!open) {
      setClientSecret(null);
      setError(null);
      setLoading(false);
      return;
    }
    setClientSecret(null);
    setError(null);
    if (selectedPackageId) {
      requestClientSecret();
    }
  }, [open, selectedPackageId, amountCents, requestClientSecret]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Subscribe to {selectedPackageName}</DialogTitle>
      <DialogContent>
        {clientSecret ? (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm clientSecret={clientSecret} />
          </Elements>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={2}>
            {loading && <CircularProgress />}
            {!loading && error && (
              <>
                <Typography color="error" align="center">{error}</Typography>
                <Box display="flex" gap={1}>
                  <Button variant="outlined" onClick={requestClientSecret}>Retry</Button>
                  <Button variant="text" onClick={onClose}>Close</Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubscribeModal;
